import { useState } from 'react';
import {
  SlidersHorizontal,
  Cpu,
  ShieldCheck,
  Bell,
  UserCircle,
  Info,
  Palette,
  Globe,
  Lock,
  Check,
  ChevronDown,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SettingsScreenProps {
  active?: boolean;
  className?: string;
}

const PREFIX = 'bnk-settings';
const NS = 'settingsScreen';

const C = {
  appBg: 'var(--bnkp-appbg, #0D1117)',
  surface: 'var(--bnkp-surface, #141B27)',
  recessed: 'var(--bnkp-recessed, #0A0F1C)',
  border: 'var(--bnkp-border, #1E2D45)',
  borderStrong: 'var(--bnkp-border2, #30415F)',
  text: 'var(--bnkp-text, #E2E8F0)',
  textSec: 'var(--bnkp-text2, #94A3C2)',
  textTer: 'var(--bnkp-text3, #4A5568)',
  primary: 'var(--bnkp-primary, #2D7EFF)',
  primaryHover: 'var(--bnkp-primary2, #1F6BEF)',
  cyan: 'var(--bnkp-cyan, #00D4FF)',
  green: 'var(--bnkp-green, #34D399)',
} as const;

type CategoryId = 'general' | 'models' | 'security' | 'notifications' | 'account' | 'about';

const CATEGORIES: readonly { id: CategoryId; icon: typeof Cpu }[] = [
  { id: 'general', icon: SlidersHorizontal },
  { id: 'models', icon: Cpu },
  { id: 'security', icon: ShieldCheck },
  { id: 'notifications', icon: Bell },
  { id: 'account', icon: UserCircle },
  { id: 'about', icon: Info },
];

type ThemeChoice = 'light' | 'dark' | 'system';

/** A small on/off pill switch. `locked` renders a guaranteed-on, non-toggleable state. */
function Toggle({
  on,
  onChange,
  locked,
  label,
}: {
  on: boolean;
  onChange?: () => void;
  locked?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      className={`${PREFIX}-switch${on ? ` ${PREFIX}-switch--on` : ''}${locked ? ` ${PREFIX}-switch--locked` : ''}`}
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={locked ? undefined : onChange}
      disabled={locked}
    >
      <span className={`${PREFIX}-switch-knob`} aria-hidden="true" />
    </button>
  );
}

/**
 * SettingsScreen — a faithful "Einstellungen" page for the bunker.ai product
 * tour: a category rail on the left and grouped configuration cards on the right
 * (appearance, local AI model, security & data sovereignty, notifications). All
 * copy is read from the `demo` i18n namespace under `settingsScreen.*`.
 */
export function SettingsScreen({ active = true, className }: SettingsScreenProps) {
  const { t } = useTranslation('demo');
  const k = (field: string) => `${NS}.${field}`;

  const [activeCat, setActiveCat] = useState<CategoryId>('general');
  const [theme, setTheme] = useState<ThemeChoice>('dark');
  const [encryption, setEncryption] = useState(true);
  const [audit, setAudit] = useState(true);
  const [twoFa, setTwoFa] = useState(false);
  const [emailNotif, setEmailNotif] = useState(true);
  const [weeklyReport, setWeeklyReport] = useState(false);

  const themeChoices: { id: ThemeChoice; labelKey: string }[] = [
    { id: 'light', labelKey: 'appearance.themeLight' },
    { id: 'dark', labelKey: 'appearance.themeDark' },
    { id: 'system', labelKey: 'appearance.themeSystem' },
  ];

  return (
    <div className={`${PREFIX}-root${active ? ` ${PREFIX}-active` : ''}${className ? ` ${className}` : ''}`}>
      <style>{css}</style>

      {/* ── Header ── */}
      <header className={`${PREFIX}-header`}>
        <span className={`${PREFIX}-icon-pill`} aria-hidden="true">
          <SlidersHorizontal size={20} color={C.primary} strokeWidth={2.2} />
        </span>
        <div className={`${PREFIX}-header-text`}>
          <h1 className={`${PREFIX}-h1`}>{t(k('title'))}</h1>
          <p className={`${PREFIX}-subtitle`}>{t(k('subtitle'))}</p>
        </div>
      </header>

      {/* ── Body: category rail + settings cards ── */}
      <div className={`${PREFIX}-body`}>
        <nav className={`${PREFIX}-cats`} aria-label={t(k('navLabel'))}>
          {CATEGORIES.map(({ id, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`${PREFIX}-cat${activeCat === id ? ` ${PREFIX}-cat--active` : ''}`}
              aria-current={activeCat === id ? 'true' : undefined}
              onClick={() => setActiveCat(id)}
            >
              <Icon size={17} strokeWidth={2} aria-hidden="true" />
              <span>{t(k(`categories.${id}`))}</span>
            </button>
          ))}
        </nav>

        <div className={`${PREFIX}-panel`}>
          <div className={`${PREFIX}-panel-scroll`}>
            {/* Appearance */}
            <section className={`${PREFIX}-card`}>
              <div className={`${PREFIX}-card-head`}>
                <span className={`${PREFIX}-card-icon`} aria-hidden="true">
                  <Palette size={16} color={C.primary} strokeWidth={2} />
                </span>
                <div>
                  <h2 className={`${PREFIX}-card-title`}>{t(k('appearance.title'))}</h2>
                  <p className={`${PREFIX}-card-desc`}>{t(k('appearance.desc'))}</p>
                </div>
              </div>

              <div className={`${PREFIX}-row`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('appearance.themeLabel'))}</div>
                </div>
                <div className={`${PREFIX}-seg`} role="group" aria-label={t(k('appearance.themeLabel'))}>
                  {themeChoices.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-on={theme === c.id}
                      onClick={() => setTheme(c.id)}
                    >
                      {t(k(c.labelKey))}
                    </button>
                  ))}
                </div>
              </div>

              <div className={`${PREFIX}-row`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('appearance.languageLabel'))}</div>
                </div>
                <div className={`${PREFIX}-select`} aria-hidden="true">
                  <Globe size={14} strokeWidth={2} />
                  <span>{t(k('appearance.languageValue'))}</span>
                  <ChevronDown size={14} strokeWidth={2} />
                </div>
              </div>
            </section>

            {/* AI Model */}
            <section className={`${PREFIX}-card`}>
              <div className={`${PREFIX}-card-head`}>
                <span className={`${PREFIX}-card-icon`} aria-hidden="true">
                  <Cpu size={16} color={C.primary} strokeWidth={2} />
                </span>
                <div>
                  <h2 className={`${PREFIX}-card-title`}>{t(k('model.title'))}</h2>
                  <p className={`${PREFIX}-card-desc`}>{t(k('model.desc'))}</p>
                </div>
              </div>

              <div className={`${PREFIX}-model`}>
                <span className={`${PREFIX}-model-mark`} aria-hidden="true">
                  <Sparkles size={18} color={C.cyan} strokeWidth={2} />
                </span>
                <div className={`${PREFIX}-model-text`}>
                  <div className={`${PREFIX}-model-top`}>
                    <span className={`${PREFIX}-model-name`}>{t(k('model.activeName'))}</span>
                    <span className={`${PREFIX}-chip`}>
                      <Lock size={11} strokeWidth={2.4} aria-hidden="true" />
                      {t(k('model.badge'))}
                    </span>
                  </div>
                  <div className={`${PREFIX}-model-meta`}>{t(k('model.activeMeta'))}</div>
                </div>
                <button type="button" className={`${PREFIX}-btn-ghost`}>{t(k('model.changeBtn'))}</button>
              </div>

              <div className={`${PREFIX}-row`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('model.tempLabel'))}</div>
                </div>
                <div className={`${PREFIX}-slider-wrap`}>
                  <span className={`${PREFIX}-slider-end`}>{t(k('model.tempPrecise'))}</span>
                  <div className={`${PREFIX}-slider`} aria-hidden="true">
                    <span className={`${PREFIX}-slider-fill`} />
                    <span className={`${PREFIX}-slider-thumb`} />
                  </div>
                  <span className={`${PREFIX}-slider-end`}>{t(k('model.tempCreative'))}</span>
                </div>
              </div>
            </section>

            {/* Security & data sovereignty */}
            <section className={`${PREFIX}-card`}>
              <div className={`${PREFIX}-card-head`}>
                <span className={`${PREFIX}-card-icon`} aria-hidden="true">
                  <ShieldCheck size={16} color={C.green} strokeWidth={2} />
                </span>
                <div>
                  <h2 className={`${PREFIX}-card-title`}>{t(k('security.title'))}</h2>
                  <p className={`${PREFIX}-card-desc`}>{t(k('security.desc'))}</p>
                </div>
              </div>

              <div className={`${PREFIX}-toggle-row ${PREFIX}-toggle-row--locked`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('security.localTitle'))}</div>
                  <div className={`${PREFIX}-row-desc`}>{t(k('security.localDesc'))}</div>
                </div>
                <span className={`${PREFIX}-guarantee`}>
                  <Check size={12} strokeWidth={3} aria-hidden="true" />
                  {t(k('security.localLocked'))}
                </span>
              </div>

              <div className={`${PREFIX}-toggle-row`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('security.encryptionTitle'))}</div>
                  <div className={`${PREFIX}-row-desc`}>{t(k('security.encryptionDesc'))}</div>
                </div>
                <Toggle on={encryption} onChange={() => setEncryption((v) => !v)} label={t(k('security.encryptionTitle'))} />
              </div>

              <div className={`${PREFIX}-toggle-row`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('security.auditTitle'))}</div>
                  <div className={`${PREFIX}-row-desc`}>{t(k('security.auditDesc'))}</div>
                </div>
                <Toggle on={audit} onChange={() => setAudit((v) => !v)} label={t(k('security.auditTitle'))} />
              </div>

              <div className={`${PREFIX}-toggle-row`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('security.twoFaTitle'))}</div>
                  <div className={`${PREFIX}-row-desc`}>{t(k('security.twoFaDesc'))}</div>
                </div>
                <Toggle on={twoFa} onChange={() => setTwoFa((v) => !v)} label={t(k('security.twoFaTitle'))} />
              </div>
            </section>

            {/* Notifications */}
            <section className={`${PREFIX}-card`}>
              <div className={`${PREFIX}-card-head`}>
                <span className={`${PREFIX}-card-icon`} aria-hidden="true">
                  <Bell size={16} color={C.primary} strokeWidth={2} />
                </span>
                <div>
                  <h2 className={`${PREFIX}-card-title`}>{t(k('notifications.title'))}</h2>
                  <p className={`${PREFIX}-card-desc`}>{t(k('notifications.desc'))}</p>
                </div>
              </div>

              <div className={`${PREFIX}-toggle-row`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('notifications.emailTitle'))}</div>
                  <div className={`${PREFIX}-row-desc`}>{t(k('notifications.emailDesc'))}</div>
                </div>
                <Toggle on={emailNotif} onChange={() => setEmailNotif((v) => !v)} label={t(k('notifications.emailTitle'))} />
              </div>

              <div className={`${PREFIX}-toggle-row`}>
                <div className={`${PREFIX}-row-text`}>
                  <div className={`${PREFIX}-row-title`}>{t(k('notifications.reportTitle'))}</div>
                  <div className={`${PREFIX}-row-desc`}>{t(k('notifications.reportDesc'))}</div>
                </div>
                <Toggle on={weeklyReport} onChange={() => setWeeklyReport((v) => !v)} label={t(k('notifications.reportTitle'))} />
              </div>
            </section>

            <p className={`${PREFIX}-footer`}>
              <Check size={13} strokeWidth={2.4} aria-hidden="true" />
              {t(k('footerNote'))}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsScreen;

const css = `
.${PREFIX}-root { font-family: Inter, system-ui, sans-serif; color: ${C.text}; display: flex; flex-direction: column; height: 100%; min-height: 0; padding: 22px 24px; }
.${PREFIX}-root * { box-sizing: border-box; }

.${PREFIX}-header, .${PREFIX}-body { opacity: 1; transform: none; }
.${PREFIX}-active .${PREFIX}-header { animation: ${PREFIX}-fade .5s ease both; }
.${PREFIX}-active .${PREFIX}-body { animation: ${PREFIX}-rise .55s cubic-bezier(.2,.7,.2,1) .06s both; }
@keyframes ${PREFIX}-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes ${PREFIX}-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

.${PREFIX}-header { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; flex: 0 0 auto; }
.${PREFIX}-icon-pill { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 11px; background: rgba(45,126,255,0.10); border: 1px solid rgba(45,126,255,0.22); flex-shrink: 0; }
.${PREFIX}-header-text { min-width: 0; }
.${PREFIX}-h1 { margin: 0; font-size: 21px; font-weight: 700; color: ${C.text}; letter-spacing: -0.01em; }
.${PREFIX}-subtitle { margin: 2px 0 0; font-size: 13px; color: ${C.textSec}; }

.${PREFIX}-body { display: grid; grid-template-columns: 218px minmax(0, 1fr); gap: 18px; flex: 1 1 auto; min-height: 0; }

/* ── Category rail ── */
.${PREFIX}-cats { display: flex; flex-direction: column; gap: 4px; min-height: 0; }
.${PREFIX}-cat { display: flex; align-items: center; gap: 11px; width: 100%; text-align: left; padding: 10px 12px; border-radius: 10px; border: 1px solid transparent; background: transparent; color: ${C.textSec}; font-family: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer; transition: background .15s, color .15s, border-color .15s; }
.${PREFIX}-cat:hover { background: rgba(255,255,255,0.04); color: ${C.text}; }
.${PREFIX}-cat--active { background: rgba(45,126,255,0.12); border-color: rgba(45,126,255,0.32); color: #BFD6FF; }
.${PREFIX}-cat--active svg { color: ${C.primary}; }

/* ── Settings panel ── */
.${PREFIX}-panel { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 14px; min-height: 0; display: flex; box-shadow: 0 1px 2px rgba(0,0,0,0.3); }
.${PREFIX}-panel-scroll { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 16px; scrollbar-width: none; }
.${PREFIX}-panel-scroll::-webkit-scrollbar { width: 0; height: 0; }

.${PREFIX}-card { background: ${C.recessed}; border: 1px solid ${C.border}; border-radius: 13px; padding: 16px 18px; }
.${PREFIX}-card-head { display: flex; align-items: flex-start; gap: 11px; margin-bottom: 14px; }
.${PREFIX}-card-icon { display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 9px; background: rgba(45,126,255,0.10); border: 1px solid rgba(45,126,255,0.20); flex-shrink: 0; margin-top: 1px; }
.${PREFIX}-card-title { margin: 0; font-size: 14.5px; font-weight: 700; color: ${C.text}; }
.${PREFIX}-card-desc { margin: 2px 0 0; font-size: 12px; color: ${C.textSec}; line-height: 1.45; }

/* generic setting row (label | control) */
.${PREFIX}-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 11px 0; border-top: 1px solid ${C.border}; flex-wrap: wrap; }
.${PREFIX}-row:first-of-type { border-top: none; }
.${PREFIX}-row-text { min-width: 0; }
.${PREFIX}-row-title { font-size: 13px; font-weight: 600; color: ${C.text}; }
.${PREFIX}-row-desc { font-size: 11.5px; color: ${C.textSec}; margin-top: 2px; line-height: 1.45; }

/* segmented control (theme) */
.${PREFIX}-seg { display: inline-flex; border-radius: 9px; overflow: hidden; border: 1px solid ${C.border}; background: ${C.appBg}; flex-shrink: 0; }
.${PREFIX}-seg button { padding: 7px 13px; border: 0; cursor: pointer; font-family: inherit; font-size: 12.5px; font-weight: 600; background: transparent; color: ${C.textSec}; transition: background .15s, color .15s; }
.${PREFIX}-seg button:hover { color: ${C.text}; }
.${PREFIX}-seg button[data-on='true'] { background: ${C.primary}; color: #fff; }

/* faux select (language) */
.${PREFIX}-select { display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 9px; border: 1px solid ${C.border}; background: ${C.appBg}; color: ${C.text}; font-size: 12.5px; font-weight: 600; flex-shrink: 0; }
.${PREFIX}-select svg:first-child { color: ${C.textSec}; }
.${PREFIX}-select svg:last-child { color: ${C.textTer}; }

/* active model card */
.${PREFIX}-model { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-radius: 11px; background: ${C.appBg}; border: 1px solid ${C.border}; margin-bottom: 4px; flex-wrap: wrap; }
.${PREFIX}-model-mark { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 10px; background: rgba(0,212,255,0.10); border: 1px solid rgba(0,212,255,0.24); flex-shrink: 0; }
.${PREFIX}-model-text { flex: 1 1 180px; min-width: 0; }
.${PREFIX}-model-top { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; }
.${PREFIX}-model-name { font-size: 13.5px; font-weight: 700; color: ${C.text}; }
.${PREFIX}-model-meta { font-size: 11.5px; color: ${C.textSec}; margin-top: 2px; }
.${PREFIX}-chip { display: inline-flex; align-items: center; gap: 4px; padding: 2px 9px; border-radius: 999px; font-size: 10.5px; font-weight: 700; color: ${C.green}; background: rgba(52,211,153,0.12); border: 1px solid rgba(52,211,153,0.30); }
.${PREFIX}-btn-ghost { padding: 8px 13px; border-radius: 9px; border: 1px solid ${C.border}; background: rgba(255,255,255,0.04); color: ${C.text}; font-family: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; transition: background .15s, border-color .15s; }
.${PREFIX}-btn-ghost:hover { background: rgba(45,126,255,0.12); border-color: rgba(45,126,255,0.4); }

/* temperature slider (static visual) */
.${PREFIX}-slider-wrap { display: flex; align-items: center; gap: 10px; flex: 1 1 240px; min-width: 200px; justify-content: flex-end; }
.${PREFIX}-slider-end { font-size: 11px; color: ${C.textSec}; white-space: nowrap; }
.${PREFIX}-slider { position: relative; flex: 1 1 auto; max-width: 200px; height: 5px; border-radius: 999px; background: ${C.border}; }
.${PREFIX}-slider-fill { position: absolute; left: 0; top: 0; bottom: 0; width: 42%; border-radius: 999px; background: linear-gradient(90deg, ${C.primary}, ${C.cyan}); }
.${PREFIX}-slider-thumb { position: absolute; left: 42%; top: 50%; transform: translate(-50%, -50%); width: 14px; height: 14px; border-radius: 50%; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.5); }

/* toggle row */
.${PREFIX}-toggle-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px 0; border-top: 1px solid ${C.border}; }
.${PREFIX}-toggle-row:first-of-type { border-top: none; }

/* switch */
.${PREFIX}-switch { position: relative; width: 40px; height: 23px; border-radius: 999px; border: 1px solid ${C.borderStrong}; background: ${C.appBg}; cursor: pointer; flex-shrink: 0; padding: 0; transition: background .18s, border-color .18s; }
.${PREFIX}-switch-knob { position: absolute; left: 2px; top: 50%; transform: translateY(-50%); width: 17px; height: 17px; border-radius: 50%; background: ${C.textSec}; transition: transform .18s, background .18s; }
.${PREFIX}-switch--on { background: rgba(45,126,255,0.22); border-color: ${C.primary}; }
.${PREFIX}-switch--on .${PREFIX}-switch-knob { transform: translate(17px, -50%); background: ${C.primary}; }
.${PREFIX}-switch--locked { cursor: default; opacity: 0.85; }

/* guarantee pill (locked-on, non-toggle) */
.${PREFIX}-guarantee { display: inline-flex; align-items: center; gap: 5px; padding: 5px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 700; color: ${C.green}; background: rgba(52,211,153,0.12); border: 1px solid rgba(52,211,153,0.30); white-space: nowrap; flex-shrink: 0; }

.${PREFIX}-footer { display: flex; align-items: center; gap: 7px; margin: 2px 0 0; font-size: 12px; color: ${C.textSec}; }
.${PREFIX}-footer svg { color: ${C.green}; }

/* collapse the category rail on narrow viewports */
@media (max-width: 1080px) {
  .${PREFIX}-body { grid-template-columns: 1fr; }
  .${PREFIX}-cats { flex-direction: row; flex-wrap: wrap; gap: 6px; }
  .${PREFIX}-cat { width: auto; }
}
@media (prefers-reduced-motion: reduce) {
  .${PREFIX}-active .${PREFIX}-header,
  .${PREFIX}-active .${PREFIX}-body { animation: none; }
}
`;
